
































































































import { Component, Prop, PropSync, Ref, Watch } from 'nuxt-property-decorator'
import { ComponentVue } from '@/base'
import { ButtonToggle } from '@/components/molecules/ButtonToggle'
import { UserFamily } from '@/api/models/user'
import { Alert, AlertMessage, ErrorMessage, userApi } from '@/api'
import {
  alertStore,
  modalErrorStore,
  systemParameterStore
} from '@/utils/store-accessor'
import { AlertFactory } from '@/factory'
import { ModalEditMemoAndNote } from '@/components/modals/ModalEditMemoAndNote'
import { ModalFamilyUsageSituation } from '@/components/modals/ModalFamilyUsageSituation'
import { FamilyUsageSituationTable } from '@/components/organisms/FamilyUsageSituationTable'
import {
  AlertCodeAL400003,
  AlertCodeILC00031,
  AlertCodeTL400020,
  AlertCodeTL400035,
  AlertCodeTLC00003,
  UserDetailStateInfo
} from '@/types'
import { InputUserId } from '@/components/molecules/InputUserId'

export interface UserContentFamilyItem {
  id: string
  userFamilyId: number | null
  familyUsers: UserFamily[]
}

export const defaultUserContentFamilyItem = (): UserContentFamilyItem => ({
  id: '',
  userFamilyId: null,
  familyUsers: []
})

@Component({
  components: {
    InputUserId,
    FamilyUsageSituationTable,
    ModalFamilyUsageSituation,
    ModalEditMemoAndNote,
    ButtonToggle
  }
})
export default class UserContentFamily extends ComponentVue {
  @Ref()
  inputUserId!: InputUserId

  @Ref()
  modalEditMemosAndNote!: ModalEditMemoAndNote

  @Ref()
  modalFamilyUsageSituation!: ModalFamilyUsageSituation

  @PropSync('item', {
    type: Object,
    required: true
  })
  contentItem!: UserContentFamilyItem

  @Prop({
    type: Object,
    required: true
  })
  state!: UserDetailStateInfo

  @Prop({
    type: Number,
    required: true
  })
  height!: number

  @Prop({
    type: Boolean,
    default: false
  })
  familyLendButton!: boolean

  inputFamilyUserId: string = ''

  selectedUser: UserFamily | null = null

  selectedUserMemo: UserFamily | null = null

  selectedUserNote: UserFamily | null = null

  get isManagementFamily(): boolean {
    return systemParameterStore.isManagementFamily
  }

  get isManagementFamilyHomePhoneNumber(): boolean {
    return systemParameterStore.isManagementFamilyHomePhoneNumber
  }

  @Watch('contentItem.familyUsers', { immediate: true })
  onChangeUserFamily() {
    if (this.contentItem.familyUsers.length > 0) {
      // 表示が逆順なので最初の選択値は末尾となる
      // eslint-disable-next-line standard/computed-property-even-spacing
      this.selectedUser = this.contentItem.familyUsers[
        this.contentItem.familyUsers.length - 1
      ]
    }
  }

  openModalUserSearch() {
    this.$modalUserSearch.open({
      onClickOk: (userId: string) => {
        this.fetchFamilyUser(userId)
      }
    })
  }

  onSelect(user: UserFamily) {
    this.selectedUser = user
  }

  onClickFamilyLend() {
    if (this.selectedUser) {
      this.$router.push(`/lend?user_id=${this.selectedUser.base.id}`)
    }
  }

  onClickMemo(value: UserFamily) {
    this.selectedUserMemo = value
    this.$nextTick(() => {
      this.modalEditMemosAndNote.open()
    })
  }

  onClickDetail(value: UserFamily) {
    this.selectedUserNote = value
    this.$nextTick(() => {
      this.modalFamilyUsageSituation.open()
    })
  }

  async onMemoAndNoteUpdated() {
    try {
      this.contentItem.familyUsers = await userApi.getUsersFamily(
        this.contentItem.id
      )

      this.$emit('updated')
    } catch (error) {
      if (error instanceof AlertMessage) {
        alertStore.setItems(error.alerts)
        this.$modalAlert.open({
          modalType: 'ERROR'
        })
      } else if (error instanceof ErrorMessage) {
        modalErrorStore.init(error)
        this.$modalError.open()
      } else {
        console.error(error)
      }
    }
  }

  async fetchFamilyUser(newUserId: string) {
    try {
      if (!this.checkNewUserId(newUserId)) {
        return
      }

      const newFamilyUser = await userApi.getUser({
        id: newUserId
      })

      // TL400035 利用者は団体利用者のため、家族登録はできません。
      if (newFamilyUser.group.name !== '') {
        this.modalAlert(AlertCodeTL400035, '家族登録')
        return
      }

      // TL400020 他の利用者と家族登録されているため、処理できません。
      if (
        newFamilyUser.personal.userFamilyId !== null &&
        this.contentItem.userFamilyId !== null
      ) {
        this.modalAlert(AlertCodeTL400020)
        return
      }

      // AlertCodeAL400003 = 'AL400003' // 家族番号が登録されています。処理を続行しますか。
      if (newFamilyUser.personal.userFamilyId !== null) {
        const alert: Alert = AlertFactory.create(AlertCodeAL400003)
        alertStore.setItem(alert)
        this.$modalAlert.open({
          onOkClick: () => {
            if (
              newFamilyUser.personal.userFamilyId !== null &&
              this.state.isShow()
            ) {
              this.contentItem.userFamilyId =
                newFamilyUser.personal.userFamilyId
            }
            this.addUserFamily(newFamilyUser)
            newFamilyUser.familyUsers.forEach(user => {
              this.addUserFamily(user)
            })
          }
        })
      } else {
        this.addUserFamily(newFamilyUser)
      }
    } catch (error) {
      if (error instanceof AlertMessage) {
        alertStore.setItems(error.alerts)
        this.inputFamilyUserId = ''
        this.$modalAlert.open()
      } else if (error instanceof ErrorMessage) {
        modalErrorStore.init(error)
        this.$modalError.open()
      } else {
        console.error(error)
      }
    }
  }

  checkNewUserId(newUserId: string): boolean {
    // TLC00003 {利用者本人}のため、{家族登録}はできません。
    if (newUserId === this.contentItem.id) {
      this.modalAlert(AlertCodeTLC00003, '利用者本人', '家族登録')
      return false
    }

    // ILC00031 入力された{利用者番号}は既に入力済みです。
    const familyUser = this.contentItem.familyUsers.find((item: UserFamily) => {
      return item.base.id === newUserId
    })
    if (familyUser !== undefined) {
      this.modalAlert(AlertCodeILC00031, '利用者番号')
      return false
    }

    return true
  }

  addUserFamily(familyUser: UserFamily) {
    this.contentItem.familyUsers.push(familyUser)
    this.inputFamilyUserId = ''
    this.inputUserId.focus()
  }

  modalAlert(code: string, ...args: string[]) {
    const alert: Alert = AlertFactory.create(code, ...args)
    alertStore.setItem(alert)
    this.$modalAlert.open({
      onOkClick: () => {
        this.inputFamilyUserId = ''
        this.inputUserId.focus()
      }
    })
  }
}
